import { createReducer, on } from '@ngrx/store';
import { CopilotActions } from './copilot.actions';
import { Conversation, Message } from '../models/copilot.models';

export interface CopilotClientState {
  conversations: Conversation[];
  messages: { [conversationId: string]: Message[] };
  selectedConversationId: string | null;
  lastActiveConversationId?: string;
  lastArchivedConversationId?: string;
  isStreaming: { [conversationId: string]: boolean };
  inputTextByConversation: { [conversationId: string]: string };
  /** Per-conversation chat message search (query + active result index). */
  chatSearchByConversation?: {
    [conversationId: string]: { query: string; activeMatchIndex: number };
  };
}

export interface CopilotState {
  clients: {
    [clientId: string]: CopilotClientState;
  };
  selectedClientId: string | null;
  highlightedClientIds: Set<string>;
  highlightedConversations: Set<string>;

}

const initialState: CopilotState = {
  clients: {},
  selectedClientId: null,
  highlightedClientIds: new Set(),
  highlightedConversations: new Set()
};

/** Empty client slice used when handlers touch a client before conversations are loaded. */
function defaultClientState(): CopilotClientState {
  return {
    conversations: [],
    messages: {},
    selectedConversationId: null,
    isStreaming: {},
    inputTextByConversation: {},
    chatSearchByConversation: {},
  };
}

/** Returns existing client state or defaults so spread merges never leave required fields undefined. */
function clientState(state: CopilotState, clientId: string): CopilotClientState {
  return state.clients[clientId] ?? defaultClientState();
}

export const copilotReducer = createReducer(
  initialState,

  on(CopilotActions.selectClient, (state, { clientId }) => ({
    ...state,
    selectedClientId: clientId
  })),
  //
  // on(CopilotActions.selectConversation, (state, { clientId, conversationId }) => ({
  //   ...state,
  //   clients: {
  //     ...state.clients,
  //     [clientId]: {
  //       ...state.clients[clientId],
  //       selectedConversationId: conversationId
  //     }
  //   }
  // })),

  on(CopilotActions.selectConversation, (state, { clientId, conversationId, view }) => {
    const client = clientState(state, clientId);

    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          selectedConversationId: conversationId,
          ...(view === 'active' && { lastActiveConversationId: conversationId }),
          ...(view === 'archived' && { lastArchivedConversationId: conversationId }),
        }
      }
    };
  }),


on(CopilotActions.loadConversationsSuccess, (state, { clientId, conversations }) => ({
    ...state,
    clients: {
      ...state.clients,
      [clientId]: {
        conversations,
        messages: {},
        selectedConversationId: null,
        isStreaming: {},
        inputTextByConversation: {},
        chatSearchByConversation: {},
      }
    }
  })),

  on(CopilotActions.loadMessagesSuccess, (state, { clientId, conversationId, messages }) => {
    const client = clientState(state, clientId);
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          messages: {
            ...client.messages,
            [conversationId]: messages
          }
        }
      }
    };
  }),

  on(CopilotActions.messageReceived, (state, { clientId, conversationId, message }) => {
    const client = clientState(state, clientId);
    const oldMessages = client.messages[conversationId] || [];
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          messages: {
            ...client.messages,
            [conversationId]: [...oldMessages, message]
          }
        }
      }
    };
  }),

  on(CopilotActions.startStreaming, (state, { clientId, conversationId }) => {
    const client = clientState(state, clientId);
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          isStreaming: {
            ...client.isStreaming,
            [conversationId]: true
          }
        }
      }
    };
  }),

  on(CopilotActions.stopStreaming, (state, { clientId, conversationId }) => {
    const client = clientState(state, clientId);
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          isStreaming: {
            ...client.isStreaming,
            [conversationId]: false
          }
        }
      }
    };
  }),

  on(CopilotActions.triggerClientHighlight, (state, { clientId }) => {
    const next = new Set(state.highlightedClientIds);
    next.add(clientId);

    return {
      ...state,
      highlightedClientIds: next
    };
  }),

  on(CopilotActions.clearClientHighlight, (state, { clientId }) => {
    const next = new Set(state.highlightedClientIds);
    next.delete(clientId);

    return {
      ...state,
      highlightedClientIds: next
    };
  }),
  on(CopilotActions.clearClient, (state, { clientId }) => {
    const updatedClients = { ...state.clients };
    delete updatedClients[clientId];

    const updatedHighlighted = new Set(state.highlightedClientIds);
    updatedHighlighted.delete(clientId);

    return {
      ...state,
      clients: updatedClients,
      highlightedClientIds: updatedHighlighted,
      selectedClientId: state.selectedClientId === clientId ? null : state.selectedClientId
    };
  }),
  on(CopilotActions.messageUpdated, (state, { clientId, conversationId, messageId, content, traces, metadata }) => {
    const client = clientState(state, clientId);
    const messages = client.messages[conversationId] || [];

    const updatedMessages = messages.map((msg) => {
      if (msg.id !== messageId) return msg;

      const newMetadata = {
        ...msg.metadata,
        ...metadata
      };

      const updatedMsg = {
        ...msg,
        content,
        metadata: newMetadata,
        ...(traces ? { traces } : {})
      };

      // Replace temp ID with final ID if finalized
      if (newMetadata.finalized && newMetadata['finalId']) {
        updatedMsg.id = newMetadata['finalId'];
      }

      return updatedMsg;
    });

    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          messages: {
            ...client.messages,
            [conversationId]: updatedMessages
          }
        }
      }
    };
  }),
  on(CopilotActions.updateInputText, (state, { clientId, conversationId, text }) => {
    const client = clientState(state, clientId);
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          inputTextByConversation: {
            ...client.inputTextByConversation,
            [conversationId]: text
          }
        }
      }
    };
  }),

  on(CopilotActions.clearInputText, (state, { clientId, conversationId }) => {
    const client = clientState(state, clientId);
    const { [conversationId]: _, ...rest } = client.inputTextByConversation;
    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          inputTextByConversation: rest
        }
      }
    };
  }),

  on(CopilotActions.triggerConversationHighlight, (state, { clientId, conversationId }) => {
    const key = `${clientId}:${conversationId}`;
    const next = new Set(state.highlightedConversations);
    next.add(key);
    return {
      ...state,
      highlightedConversations: next
    };
  }),

  on(CopilotActions.clearConversationHighlight, (state, { clientId, conversationId }) => {
    const key = `${clientId}:${conversationId}`;
    const next = new Set(state.highlightedConversations);
    next.delete(key);
    return {
      ...state,
      highlightedConversations: next
    };
  }),

  on(
    CopilotActions.updateChatSearchQuery,
    (state, { clientId, conversationId, query }) => {
      const client = state.clients[clientId];
      if (!client) return state;

      return {
        ...state,
        clients: {
          ...state.clients,
          [clientId]: {
            ...client,
            chatSearchByConversation: {
              ...(client.chatSearchByConversation ?? {}),
              [conversationId]: { query, activeMatchIndex: 0 },
            },
          },
        },
      };
    }
  ),

  on(
    CopilotActions.setChatSearchActiveMatchIndex,
    (state, { clientId, conversationId, activeMatchIndex }) => {
      const client = state.clients[clientId];
      if (!client) return state;
      const prev = client.chatSearchByConversation?.[conversationId];
      const query = prev?.query ?? '';

      return {
        ...state,
        clients: {
          ...state.clients,
          [clientId]: {
            ...client,
            chatSearchByConversation: {
              ...(client.chatSearchByConversation ?? {}),
              [conversationId]: { query, activeMatchIndex },
            },
          },
        },
      };
    }
  ),

  on(CopilotActions.clearChatSearch, (state, { clientId, conversationId }) => {
    const client = state.clients[clientId];
    if (!client) return state;

    const prev = client.chatSearchByConversation ?? {};
    const rest = { ...prev };
    delete rest[conversationId];

    return {
      ...state,
      clients: {
        ...state.clients,
        [clientId]: {
          ...client,
          chatSearchByConversation: rest,
        },
      },
    };
  }),
);

